/**
 * Proceduralne FX + dźwięki (WAVE3 G13).
 * Sample przez AudioBus.playSfx; fallback = bip WebAudio gdy sample nie gra.
 * Respektuje audioBus.sfxOn (przełącznik „Dźwięki: WYŁ.").
 */

import Phaser from 'phaser';
import { audioBus } from '../core/AudioBus.js';
import type { ObjectFxKind } from './types.js';

export type FxHandle = {
  stop: () => void;
};

export type ItemTransitionFxKind = 'pickup' | 'drop';

const DEPTH = 5000;

type ParticleSpec = {
  color: number;
  emoji?: string;
  count: number;
  spread: number;
  rise: number;
  size: number;
  duration: number;
};

const SPECS: Record<ObjectFxKind, ParticleSpec> = {
  hearts: {
    color: 0xff6b8a,
    emoji: '💖',
    count: 6,
    spread: 36,
    rise: 48,
    size: 14,
    duration: 700,
  },
  stars: {
    color: 0xffd700,
    emoji: '⭐',
    count: 7,
    spread: 42,
    rise: 40,
    size: 12,
    duration: 650,
  },
  crumbs: {
    color: 0xc4a574,
    emoji: '🍪',
    count: 8,
    spread: 28,
    rise: 18,
    size: 8,
    duration: 550,
  },
  splash: {
    color: 0x6ecbff,
    emoji: '💧',
    count: 10,
    spread: 50,
    rise: 30,
    size: 10,
    duration: 600,
  },
  notes: {
    color: 0xb388ff,
    emoji: '🎵',
    count: 5,
    spread: 40,
    rise: 56,
    size: 14,
    duration: 800,
  },
  sparkle: {
    color: 0xfff4c1,
    emoji: '✨',
    count: 8,
    spread: 38,
    rise: 36,
    size: 10,
    duration: 600,
  },
};

/** Domyślne sample per rodzaj FX. */
const FX_SFX: Record<ObjectFxKind, string> = {
  hearts: 'sfx_pet',
  stars: 'sfx_success',
  crumbs: 'sfx_click',
  splash: 'sfx_splash',
  notes: 'sfx_bell',
  sparkle: 'sfx_click',
};

/**
 * Mapowanie def.sound / aliasów na klucze sampli.
 * Nieznane nazwy → sfx_<name> jeśli wyglądają na klucz, inaczej null (tylko bip).
 */
const SOUND_ALIASES: Record<string, string> = {
  click: 'sfx_click',
  buy: 'sfx_buy',
  cash: 'sfx_buy',
  kasa: 'sfx_buy',
  success: 'sfx_success',
  cheer: 'sfx_cheer',
  reward: 'sfx_reward',
  fail: 'sfx_fail_soft',
  wrong: 'sfx_wrong',
  error: 'sfx_fail_soft',
  splash: 'sfx_splash',
  slide: 'sfx_slide',
  wee: 'sfx_slide',
  pet: 'sfx_pet',
  purr: 'sfx_pet',
  miau: 'sfx_pet',
  bell: 'sfx_bell',
  clang: 'sfx_build',
  build: 'sfx_build',
  drop: 'sfx_build',
  spin: 'sfx_spin',
  pluck: 'sfx_click',
};

/** Częstotliwości „dźwięków" proceduralnych (Hz) — fallback. */
const TONES: Record<ObjectFxKind, number[]> = {
  hearts: [523, 659, 784],
  stars: [880, 1108, 1320],
  crumbs: [220, 180],
  splash: [440, 330, 220],
  notes: [392, 494, 587, 698],
  sparkle: [988, 1174, 1480],
};

let audioCtx: AudioContext | null = null;

function getAudioCtx(): AudioContext | null {
  try {
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AC) return null;
    if (!audioCtx) audioCtx = new AC();
    if (audioCtx.state === 'suspended') {
      void audioCtx.resume();
    }
    return audioCtx;
  } catch {
    return null;
  }
}

/** Krótki bip/arpeggio bez plików audio (fallback gdy sfxOn i brak sample). */
export function playProceduralSound(kind: ObjectFxKind, volume = 0.08): void {
  if (!audioBus.sfxOn) return;
  const ctx = getAudioCtx();
  if (!ctx) return;
  const freqs = TONES[kind];
  const now = ctx.currentTime;
  freqs.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = kind === 'splash' || kind === 'crumbs' ? 'triangle' : 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(volume, now + 0.02 + i * 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18 + i * 0.05);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now + i * 0.04);
    osc.stop(now + 0.25 + i * 0.05);
  });
}

/** Rozwiąż def.sound / alias na klucz sample (lub null). */
export function resolveSfxKey(sound: string | undefined | null): string | null {
  if (!sound) return null;
  const s = sound.trim();
  if (!s) return null;
  if (SOUND_ALIASES[s]) return SOUND_ALIASES[s];
  if (s.startsWith('sfx_')) return s;
  const lower = s.toLowerCase();
  if (SOUND_ALIASES[lower]) return SOUND_ALIASES[lower];
  // np. "clang" już w aliasach; inne → spróbuj sfx_<name>
  return `sfx_${lower}`;
}

/**
 * Graj dźwięk FX: najpierw sample (AudioBus), przy sfxOn=false — cisza.
 * Proceduralny bip jako uzupełnienie / gdy nie ma sample (playSfx i tak no-op
 * tylko przy wyłączonym sfx; bip też respektuje sfxOn).
 */
export function playFxSound(
  kind: ObjectFxKind,
  sound?: string | null,
  opts?: { volume?: number },
): void {
  if (!audioBus.sfxOn) return;

  const fromDef = resolveSfxKey(sound ?? null);
  const key = fromDef ?? FX_SFX[kind];
  if (key) {
    audioBus.playSfx(key, { volume: opts?.volume ?? 0.55 });
  }
  // Lekki bip pod spodem tylko gdy nie ma zmapowanego sample (stary fallback).
  // Przy znanym kluczu sample gra sam — unikamy podwójnego „klik+bip".
  if (!key) {
    playProceduralSound(kind, opts?.volume ?? 0.08);
  }
}

/**
 * Burst cząsteczek w punkcie (serduszka / okruszki / plusk / nutki / gwiazdki / sparkle).
 * opts.sound: false = bez dźwięku; string = def.sound / alias sample.
 */
export function playFx(
  scene: Phaser.Scene,
  kind: ObjectFxKind,
  x: number,
  y: number,
  opts?: { sound?: boolean | string; depth?: number },
): FxHandle {
  const spec = SPECS[kind];
  const depth = opts?.depth ?? DEPTH;
  const nodes: Phaser.GameObjects.GameObject[] = [];

  if (opts?.sound !== false) {
    const soundName = typeof opts?.sound === 'string' ? opts.sound : null;
    playFxSound(kind, soundName);
  }

  for (let i = 0; i < spec.count; i++) {
    const ang = (i / spec.count) * Math.PI * 2 + Math.random() * 0.4;
    const dist = 8 + Math.random() * spec.spread;
    const tx = x + Math.cos(ang) * dist;
    const ty = y + Math.sin(ang) * dist - spec.rise * (0.5 + Math.random() * 0.5);

    if (spec.emoji) {
      const t = scene.add
        .text(x, y, spec.emoji, { fontSize: `${spec.size}px` })
        .setOrigin(0.5)
        .setDepth(depth);
      nodes.push(t);
      scene.tweens.add({
        targets: t,
        x: tx,
        y: ty,
        alpha: 0,
        scale: 0.3 + Math.random() * 0.4,
        duration: spec.duration + Math.random() * 120,
        ease: 'Cubic.Out',
        onComplete: () => t.destroy(),
      });
    } else {
      const c = scene.add
        .circle(x, y, spec.size / 2, spec.color, 0.95)
        .setDepth(depth);
      nodes.push(c);
      scene.tweens.add({
        targets: c,
        x: tx,
        y: ty,
        alpha: 0,
        scale: 0.2,
        duration: spec.duration,
        ease: 'Cubic.Out',
        onComplete: () => c.destroy(),
      });
    }
  }

  // błysk centralny
  const flash = scene.add.circle(x, y, 10, spec.color, 0.55).setDepth(depth - 1);
  nodes.push(flash);
  scene.tweens.add({
    targets: flash,
    scale: 2.4,
    alpha: 0,
    duration: spec.duration * 0.7,
    ease: 'Cubic.Out',
    onComplete: () => flash.destroy(),
  });

  return {
    stop: () => {
      for (const n of nodes) {
        if (n.active) n.destroy();
      }
    },
  };
}

/**
 * Mały, bez-emoji sygnał przypięcia/odpięcia przedmiotu.
 * Pickup zbiera kropki do kotwicy; drop robi krótki puf w dół, więc przedmiot
 * nie wygląda jak pozostawiony w powietrzu.
 */
export function playItemTransitionFx(
  scene: Phaser.Scene,
  kind: ItemTransitionFxKind,
  x: number,
  y: number,
  opts?: { depth?: number; sound?: boolean },
): FxHandle {
  const depth = opts?.depth ?? DEPTH;
  const color = kind === 'pickup' ? 0xffd166 : 0xd9c2a3;
  const nodes: Phaser.GameObjects.GameObject[] = [];
  const ring = scene.add.circle(x, y, 8, color, 0.18).setDepth(depth);
  ring.setStrokeStyle(2, 0xffffff, 0.85);
  nodes.push(ring);

  if (opts?.sound !== false) {
    playFxSound(kind === 'pickup' ? 'sparkle' : 'crumbs', null, {
      volume: 0.38,
    });
  }

  scene.tweens.add({
    targets: ring,
    scaleX: kind === 'pickup' ? 0.35 : 2,
    scaleY: kind === 'pickup' ? 0.35 : 0.7,
    y: kind === 'pickup' ? y - 4 : y + 12,
    alpha: 0,
    duration: kind === 'pickup' ? 220 : 280,
    ease: 'Cubic.Out',
    onComplete: () => ring.destroy(),
  });

  for (let i = 0; i < 5; i++) {
    const angle = (i / 5) * Math.PI * 2;
    const startRadius = kind === 'pickup' ? 18 : 4;
    const dot = scene.add
      .circle(
        x + Math.cos(angle) * startRadius,
        y + Math.sin(angle) * startRadius,
        2.5,
        color,
        0.9,
      )
      .setDepth(depth + 1);
    nodes.push(dot);
    scene.tweens.add({
      targets: dot,
      x:
        kind === 'pickup'
          ? x
          : x + Math.cos(angle) * (12 + i * 2),
      y:
        kind === 'pickup'
          ? y
          : y + 8 + Math.abs(Math.sin(angle)) * 8,
      alpha: 0,
      scale: 0.3,
      duration: kind === 'pickup' ? 180 : 260,
      ease: 'Quad.Out',
      onComplete: () => dot.destroy(),
    });
  }

  return {
    stop: () => {
      for (const node of nodes) {
        if (node.active) node.destroy();
      }
    },
  };
}

/** Alias mapowania dogReaction → FX gdy def.fx brak. */
export function fxForReaction(
  reaction: 'eat' | 'happy' | 'jump' | 'sniff' | string | undefined,
): ObjectFxKind {
  switch (reaction) {
    case 'eat':
      return 'crumbs';
    case 'happy':
      return 'hearts';
    case 'jump':
      return 'stars';
    case 'sniff':
      return 'sparkle';
    default:
      return 'sparkle';
  }
}
